import { computed, ref, watch } from 'vue';

/**
 * App theme.
 *
 * The colors themselves live in src/shared/styles/v2/foundation.css: a `:root` block for light and
 * a `:root[data-theme='dark']` block that overrides the same token names. All this does is decide
 * which one is active and stamp `data-theme` on <html>.
 *
 * Stored per *device*, not per user — it mirrors an OS-level preference, and someone on a shared
 * workstation shouldn't inherit the last person's theme along with their session.
 *
 * NOTE: index.html carries a copy of the resolution logic in an inline pre-paint script, so the
 * first paint already has the right theme instead of flashing light. Keep the two in sync — the
 * storage key and the meaning of 'system' are the shared contract.
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'zs:theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function readStoredPreference(): ThemePreference {
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);
        return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
    } catch {
        // Safari private mode throws on localStorage access.
        return 'system';
    }
}

const media = window.matchMedia?.(DARK_QUERY) ?? null;
const systemPrefersDark = ref(media?.matches ?? false);
media?.addEventListener('change', event => {
    systemPrefersDark.value = event.matches;
});

/** What the user picked. `system` follows the OS. */
export const themePreference = ref<ThemePreference>(readStoredPreference());

/** What is actually rendered — `system` resolved against the OS preference. */
export const resolvedTheme = computed<ResolvedTheme>(() =>
    themePreference.value === 'system' ? (systemPrefersDark.value ? 'dark' : 'light') : themePreference.value
);

export function setThemePreference(preference: ThemePreference) {
    themePreference.value = preference;
    try {
        localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
        // Non-persistent is better than broken; the in-memory ref still drives this tab.
    }
}

/** Menu order, and the labels for it. `system` is named for what it does, not what it is. */
export const THEME_OPTIONS: ThemePreference[] = ['system', 'light', 'dark'];

export const THEME_LABELS: Record<ThemePreference, string> = {
    system: 'Auto (System)',
    light: 'Light',
    dark: 'Dark'
};

// Another tab changed the preference — follow it, so a user with the app open twice doesn't see
// two different themes.
window.addEventListener('storage', event => {
    if (event.key === THEME_STORAGE_KEY) themePreference.value = readStoredPreference();
});

function applyTheme(theme: ResolvedTheme) {
    document.documentElement.dataset.theme = theme;

    // Colors the browser chrome on mobile Safari/Chrome. Reads the resolved canvas token rather
    // than a literal so it can't drift from the palette.
    const canvas = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
    if (canvas) {
        let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
        if (!meta) {
            meta = document.createElement('meta');
            meta.name = 'theme-color';
            document.head.appendChild(meta);
        }
        meta.content = canvas;
    }
}

/** Called once during app bootstrap. */
export function initTheme() {
    watch(resolvedTheme, applyTheme, { immediate: true });
}
